import { Box, Typography, MenuItem } from '@mui/material';
import { FC, useCallback } from 'react';
import { useForm, FormProvider, FieldError } from 'react-hook-form';
import { CoreButton } from '../../../_core/components/_ui/core-button';
import { CoreModal } from '../../../_core/components/_ui/core-modal';
import { CoreSelect } from '../../../_core/components/_ui/core-select';
import { InputComponentByType } from './input-component-by-type.component';
import { CustomFieldDto, EngagementDto, EngagementStatusDto } from '@families-link/shared';
import { useTranslation } from 'react-i18next';
import { buttonModalWindowStyles, mainTitleModalWindowStyles } from '../../../_core/styles';
import { getDefaultFormFields } from '../../utils/getDefaultFormFields.util';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { CoreTextField } from '../../../_core/components/_ui/core-textfield';
import { useMuiModal } from '../../../_core/hooks';
import { ConfirmationModal, ConfirmationModalProps } from '../../../_core/components/confirmation-modal';
import { mergeSx } from 'merge-sx';

export interface EngagementBulkEditFormModalProps {
	formTitle: string;
	submitAction: (data: any) => void;
	formError: any;
	closeModal: () => void;
	defaultValuesArray: any;
	statusesList?: EngagementStatusDto[];
	submitButtonName?: string;
	generateFileUrlFunction: any;
	onClose?: () => void;
}

const validateField = (value: any, field?: CustomFieldDto) => {
	if (field?.isRequired && !value) {
		return `${field?.name} is required`;
	}

	return true;
};

export const EngagementBulkEditFormModal: FC<EngagementBulkEditFormModalProps> = (props) => {
	const [t] = useTranslation();
	const {
		formTitle,
		submitAction,
		closeModal,
		defaultValuesArray,
		submitButtonName,
		statusesList,
		generateFileUrlFunction,
		onClose,
		...modalProps
	} = props;

	const defaultValues = defaultValuesArray[0];

	const methods = useForm<EngagementDto>({
		defaultValues,
		mode: 'all',
	});

	const {
		register,
		handleSubmit,
		watch,
		formState: { errors, isDirty, isLoading, isSubmitting, isSubmitted, isValid, dirtyFields },
	} = methods;

	const disableSubmit = !isValid && (isDirty || isSubmitted);
	const loadingSubmit = isLoading || isSubmitting;

	const handleFormSubmit = async (values: any) => {
		try {
			const selectedIds = defaultValuesArray.map((e: EngagementDto) => e._id);
			const updateData: Record<string, any> = {
				customFieldsValues: [],
			};
			if (dirtyFields.status) {
				updateData['status'] = values.status;
			}
			Object.keys(dirtyFields)
				.filter((key) => key !== 'status')
				.forEach((key) => {
					const rawValue = values[key];
					const fieldId = key.replace('field_', '');

					updateData['customFieldsValues'].push({
						fieldId,
						value: rawValue,
					});
				});

			await submitAction({ ids: selectedIds, updateData });
		} catch (error) {
			console.error('Error submitting bulk edit form:', error);
		}
	};

	const { openModal: openModalConfirm, closeModal: closeModalConfirm } =
		useMuiModal<ConfirmationModalProps>(ConfirmationModal);
	const handleOpenConfirmModal = useCallback(() => {
		openModalConfirm({
			title: 'There are unsaved changes',
			description: 'If you close the form, all unsaved changes will be lost',
			onSubmit: () => {
				closeModalConfirm();
				closeModal();
			},
			onClose: closeModalConfirm,
			modalWidth: 600,
			leftButtonProps: {
				children: t('engagements.forms.buttons.cancel'),
				variant: 'secondary',
				sx: buttonModalWindowStyles,
			},
			rightButtonProps: {
				children: t('engagements.forms.buttons.confirm'),
				contraModeStyleButton: true,
				sx: buttonModalWindowStyles,
			},
		});
	}, [openModalConfirm, handleSubmit]);

	const customOnClose = isDirty ? handleOpenConfirmModal : onClose;
	const customCloseFormButton = isDirty ? handleOpenConfirmModal : closeModal;

	return (
		<CoreModal
			open={false}
			{...modalProps}
			onClose={customOnClose}
			bodySx={{ p: 0, mx: 5, mb: 5 }}
			paperSx={{ width: '80%', maxWidth: '80%', mx: 'auto', overflow: 'visible' }}
		>
			<Typography
				variant="h5"
				sx={mergeSx(mainTitleModalWindowStyles, {
					position: 'sticky',
					top: 0,
					zIndex: 1,
					backgroundColor: (theme) => theme.colors.white,
				})}
			>
				{formTitle}
			</Typography>
			<FormProvider {...methods}>
				<LocalizationProvider dateAdapter={AdapterDayjs}>
					<Box component="form" onSubmit={handleSubmit(handleFormSubmit)}>
						<Box
							sx={{
								flex: 1,
								overflowX: 'auto',
								overflowY: 'auto',
							}}
						>
							{defaultValuesArray.map((engagement: EngagementDto, index: number) => {
								const fieldsList: CustomFieldDto[] = engagement.customFields || [];
								const defaultFormFields = getDefaultFormFields(fieldsList);
								const showLabel = index === 0;
								const typeInForm = `field_${defaultFormFields.type?._id}`;
								const titleField = fieldsList?.find((field) => field.name === 'Title');
								const engagementTitle = engagement?.customFieldsValues?.find(
									(value) => value.fieldId.toString() === titleField?._id.toString()
								)?.value;
								const typeField = fieldsList?.find((field) => field.name === 'Type');
								const engagementType = engagement?.customFieldsValues?.find(
									(value) => value.fieldId.toString() === typeField?._id.toString()
								)?.value;

								return (
									<Box
										key={engagement._id}
										sx={{
											display: 'flex',
											width: '100%',
											gap: '4px',
											alignItems: 'center',
											height: '100%',
										}}
									>
										<Box sx={{ flex: 1, minWidth: 100 }}>
											<CoreTextField
												value={engagementTitle}
												label={showLabel ? defaultFormFields?.title?.name : undefined}
												requiredMark={defaultFormFields?.title?.isRequired}
												fullWidth
												placeholder={t('engagements.forms.placeholders.text') ?? ''}
												controlSx={{ mb: 1 }}
												disabled
											/>
										</Box>
										<Box sx={{ flex: 1, minWidth: 180 }}>
											<CoreSelect
												label={showLabel ? t('engagements.forms.labels.type') : undefined}
												requiredMark={defaultFormFields?.type?.isRequired}
												fullWidth
												placeholder={t('engagements.forms.placeholders.options') ?? ''}
												value={engagementType}
												controlSx={{ mb: 1 }}
												disabled
											></CoreSelect>
										</Box>
										<Box sx={{ flex: 1, minWidth: 220 }}>
											<InputComponentByType
												typeInForm={typeInForm}
												customField={defaultFormFields.school}
												label={showLabel ? t('engagements.forms.labels.school') : undefined}
												hideLabel={true}
												customControlSx={{ mb: 1 }}
											/>
										</Box>
										<Box sx={{ flex: 1, minWidth: 220 }}>
											<InputComponentByType
												typeInForm={typeInForm}
												customField={defaultFormFields.program}
												label={showLabel ? t('engagements.forms.labels.program') : undefined}
												hideLabel={true}
												customControlSx={{ mb: 1 }}
											/>
										</Box>

										<Box sx={{ flex: 1, minWidth: 180, alignItems: 'center' }}>
											<CoreSelect
												label={showLabel ? t('engagements.forms.labels.status') : undefined}
												requiredMark
												fullWidth
												placeholder={t('engagements.forms.placeholders.options')}
												{...register(`status`, {
													required: `${t('engagements.forms.labels.status')} is required`,
												})}
												value={watch(`status`) || ''}
												error={!!errors[`status`]}
												helperText={(errors[`status`] as FieldError)?.message}
												controlSx={{ mb: 1 }}
											>
												{statusesList?.map((value) => (
													<MenuItem key={value._id} value={value._id}>
														{value.statusName}
													</MenuItem>
												))}
											</CoreSelect>
										</Box>
										<Box sx={{ flex: 1, minWidth: 180, alignItems: 'center' }}>
											<CoreSelect
												label={showLabel ? t('engagements.forms.labels.language') : undefined}
												requiredMark={defaultFormFields?.type?.isRequired}
												fullWidth
												placeholder={t('engagements.forms.placeholders.options') ?? ''}
												value={engagement.language}
												controlSx={{ mb: 1 }}
												disabled
											></CoreSelect>
										</Box>
									</Box>
								);
							})}
						</Box>
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'flex-end',
								alignItems: 'center',
								gap: 3,
								mt: 5,
								position: 'sticky',
								bottom: 0,
								zIndex: 1,
								backgroundColor: (theme) => theme.colors.white,
							}}
						>
							<CoreButton onClick={customCloseFormButton} variant="secondary" sx={buttonModalWindowStyles}>
								{t('families.forms.buttons.close-form')}
							</CoreButton>
							<CoreButton
								type="submit"
								contraModeStyleButton
								disabled={disableSubmit}
								loading={loadingSubmit}
								sx={buttonModalWindowStyles}
							>
								{submitButtonName ?? t('engagements.forms.buttons.save')}
							</CoreButton>
						</Box>
					</Box>
				</LocalizationProvider>
			</FormProvider>
		</CoreModal>
	);
};
